import type { TimeReportsV2Item } from "services/api/company/timeReports.service";
import { minutesBetweenIsoStrict } from "web-apps/company/utils/timeAndDate";

/**
 * Calculates net minutes from start/end minus break using strict parsing.
 * Returns null when base interval is invalid; break defaults to 0 if invalid.
 */
export interface CalculateNetMinutesParams {
  startTime: string | null | undefined;
  endTime: string | null | undefined;
  breakStartTime: string | null | undefined;
  breakEndTime: string | null | undefined;
}

export const calculateNetMinutes = (
  params: CalculateNetMinutesParams,
): number | null => {
  const totalMinutes = minutesBetweenIsoStrict(
    params.startTime,
    params.endTime,
  );
  if (totalMinutes == null) return null;
  const breakMinutes =
    minutesBetweenIsoStrict(params.breakStartTime, params.breakEndTime) ?? 0;
  return Math.max(0, totalMinutes - breakMinutes);
};

/** Calculates planned net minutes for a time-report item using strict parsing. */
export const getPlannedMinutes = (item: TimeReportsV2Item): number | null => {
  return calculateNetMinutes({
    startTime: item.plannedStartTime,
    endTime: item.plannedEndTime,
    breakStartTime: item.plannedBreakStartTime,
    breakEndTime: item.plannedBreakEndTime,
  });
};

/** Calculates reported net minutes for a time-report item using strict parsing. */
export const getReportedMinutes = (item: TimeReportsV2Item): number | null => {
  return calculateNetMinutes({
    startTime: item.reportedStartTime,
    endTime: item.reportedEndTime,
    breakStartTime: item.breakStartTime,
    breakEndTime: item.breakEndTime,
  });
};

/** Calculates stamped net minutes for a time-report item using strict parsing. */
export const getStampedMinutes = (item: TimeReportsV2Item): number | null => {
  return calculateNetMinutes({
    startTime: item.stampedStartTime,
    endTime: item.stampedEndTime,
    breakStartTime: item.breakStartTime,
    breakEndTime: item.breakEndTime,
  });
};
